import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldIcon, LockIcon, UserIcon, MailIcon, BrainIcon, DatabaseIcon, ArrowRightIcon } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/form/Button';

const privacyPoints = [
  {
    icon: <LockIcon className="h-6 w-6" />,
    title: "Tracking Only With Consent",
    description: "We use Google Analytics only after you explicitly accept cookies"
  },
  {
    icon: <DatabaseIcon className="h-6 w-6" />,
    title: "No Personal Behavior Profiling",
    description: "We analyze traffic anonymously and never build personal profiles"
  },
  {
    icon: <UserIcon className="h-6 w-6" />,
    title: "Secure Sessions",
    description: "Your session data is encrypted and securely stored"
  },
  {
    icon: <MailIcon className="h-6 w-6" />,
    title: "Minimal Data Collection",
    description: "Email address is the only personal data we require"
  },
  {
    icon: <BrainIcon className="h-6 w-6" />,
    title: "AI Privacy",
    description: "Only anonymized text is sent to AI, never your email or personal data"
  },
  {
    icon: <ShieldIcon className="h-6 w-6" />,
    title: "No AI Training",
    description: "Your data is never used to train AI models"
  }
];


const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function PrivacyProtection() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <div id="privacy-protection" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="lg:text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-center mb-4">
            <ShieldIcon className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Your Privacy is Our Priority
          </h2>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            We believe in complete transparency and maximum privacy protection. Here's how we safeguard your data:
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {privacyPoints.map((point) => (
              <motion.div
                key={point.title}
                variants={item}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 h-full"
              >
                <div className="p-8">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 text-green-600">
                      {point.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {point.title}
                      </h3>
                      <p className="mt-2 text-gray-600">
                        {point.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Button
            onClick={handleGetStarted}
            variant="primary"
            className="px-8 py-3 md:py-4 md:px-10 text-base md:text-lg"
          >
            Create SNAP
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
}